/**
 * Auto Welcome Discount Script
 * Handles URL discount codes and falls back to WELCOME discount
 * The existing cart system will handle applying it automatically
 * 
 * possible parameters:
 * - open_discount=true
 * - continue=true
 * - scroll=element-id
 * - take-the-quiz=true (redirects to quiz or your-stack based on customerEmail)
 * - discount_code=WELCOME
 * - email=address@
 */


(function() {
  'use strict';
  
  // Configuration
  const WELCOME_DISCOUNT_CODE = 'WELCOME';
  
  // Function to get and remove discount_code parameter from URL
  function getAndRemoveDiscountCodeFromURL() {
    const url = new URL(window.location.href);
    const discountCode = url.searchParams.get('discount_code');
    
    if (discountCode) {
      // Remove the discount_code parameter from URL
      url.searchParams.delete('discount_code');
      
      // Update the URL without the parameter (without page reload)
      window.history.replaceState({}, '', url.toString());
      
      console.log(`📝 Found discount code in URL: ${discountCode}`);
      return discountCode.trim();
    }
    
    return null;
  }
  
  // Function to check and store open_discount parameter
  function handleOpenDiscountParameter() {
    const url = new URL(window.location.href);
    const openDiscount = url.searchParams.get('open_discount');
    
    if (openDiscount === 'true') {
      // Store in localStorage
      try {
        localStorage.setItem('open_discount', 'true');
        console.log('📝 Stored open_discount=true in localStorage');
      } catch (error) {
        console.error('❌ Error storing open_discount in localStorage:', error);
      }
      
      // Remove the open_discount parameter from URL
      url.searchParams.delete('open_discount');
      
      // Update the URL without the parameter (without page reload)
      window.history.replaceState({}, '', url.toString());
      
      console.log('🗑️ Removed open_discount parameter from URL');
    }
  }
  
  // Function to check and handle continue parameter
  function handleContinueParameter() {
    const url = new URL(window.location.href);
    const continueParam = url.searchParams.get('continue');
    
    if (continueParam === 'true') {
      console.log('🔄 Continue parameter found in URL:', continueParam);
      
      // Store shouldATC flag in localStorage
      try {
        localStorage.setItem('shouldATC', 'true');
        console.log('📝 Stored shouldATC=true in localStorage');
      } catch (error) {
        console.error('❌ Error storing shouldATC in localStorage:', error);
      }
      
      // Remove continue from URL params
      url.searchParams.delete('continue');
      
      // Update URL without continue param
      window.history.replaceState({}, '', url.toString());
      
      console.log('✅ URL updated, continue parameter removed');
      return true;
    }
    
    return false;
  }

  // Function to handle scroll parameter
  function handleScrollParameter() {
    const url = new URL(window.location.href);
    const scrollParam = url.searchParams.get('scroll');
    
    if (scrollParam) {
      console.log('📜 Scroll parameter found in URL:', scrollParam);
      
      // Remove scroll from URL params
      url.searchParams.delete('scroll');
      
      // Update URL without scroll param
      window.history.replaceState({}, '', url.toString());
      
      console.log('✅ URL updated, scroll parameter removed');
      
      // Start scrolling to the element
      scrollToElement(scrollParam);
      
      return true;
    }
    
    return false;
  }

  // Function to handle email parameter
  function handleEmailParameter() {
    const url = new URL(window.location.href);
    const email = url.searchParams.get('email');
    
    if (email) {
      console.log('📧 Email parameter found in URL');
      
      // Store email in localStorage
      try {
        localStorage.setItem('emailMailchimp', email.trim());
        console.log(`📝 Stored email in localStorage as emailMailchimp: ${email.trim()}`);
      } catch (error) {
        console.error('❌ Error storing email in localStorage:', error);
      }
      
      // Remove email from URL params
      url.searchParams.delete('email');
      
      // Update URL without email param
      window.history.replaceState({}, '', url.toString());
      
      console.log('✅ URL updated, email parameter removed');
      return true;
    }
    
    return false;
  }

  // Function to handle take-the-quiz parameter
  function handleTakeTheQuizParameter() {
    const url = new URL(window.location.href);
    const takeTheQuiz = url.searchParams.get('take-the-quiz');
    
    if (takeTheQuiz === 'true') {
      console.log('🎯 take-the-quiz parameter found in URL');
      
      // Remove take-the-quiz from URL params
      url.searchParams.delete('take-the-quiz');
      
      // Update URL without take-the-quiz param
      window.history.replaceState({}, '', url.toString());
      
      console.log('✅ URL updated, take-the-quiz parameter removed');
      
      // Check for customerEmail in localStorage
      const customerEmail = localStorage.getItem('customerEmail');
      
      if (customerEmail) {
        console.log('📧 Customer email found in localStorage, redirecting to your-stack');
        window.location.href = 'https://www.nutricode.io/pages/your-stack';
      } else {
        console.log('📧 No customer email found, redirecting to quiz');
        window.location.href = 'https://www.nutricode.io/#quiz-4pHJwYp';
      }
      
      return true;
    }
    
    return false;
  }

  // Function to scroll to an element by ID
  function scrollToElement(elementId) {
    console.log(`🎯 Attempting to scroll to element: ${elementId}`);
    
    let attempts = 0;
    const maxAttempts = 100; // Try for 10 seconds (100 * 100ms)
    const intervalTime = 100;
    
    const scrollInterval = setInterval(() => {
      attempts++;
      
      const element = document.getElementById(elementId);
      
      if (element) {
        console.log(`✅ Found element ${elementId}, scrolling to it`);
        element.scrollIntoView({ 
          behavior: 'smooth',
          block: 'start'
        });
        clearInterval(scrollInterval);
        return;
      }
      
      if (attempts >= maxAttempts) {
        console.log(`⚠️ Could not find element ${elementId} after ${maxAttempts} attempts`);
        clearInterval(scrollInterval);
        return;
      }
      
      console.log(`🔄 Attempt ${attempts}/${maxAttempts}: Element ${elementId} not found yet, retrying...`);
    }, intervalTime);
  }
  
  // Function to remove WELCOME discount if it exists
  function removeWelcomeDiscount() {
    try {
      const appliedDiscounts = getAppliedDiscounts();
      
      if (appliedDiscounts[WELCOME_DISCOUNT_CODE]) {
        delete appliedDiscounts[WELCOME_DISCOUNT_CODE];
        saveAppliedDiscounts(appliedDiscounts);
        console.log(`🗑️ Removed WELCOME discount from localStorage`);
      }
    } catch (error) {
      console.error('❌ Error removing WELCOME discount:', error);
    }
  }
  
  // Function to apply a discount code
  async function applyDiscountCode(discountCode) {
    try {
      console.log(`🔄 Attempting to apply discount code: ${discountCode}`);
      
      const result = await globalAddDiscount(
        discountCode, 
        null, 
        "Checking...", 
        "Applied", 
        "Invalid", 
        "Applied"
      );
      
      if (result.success) {
        console.log(`✅ Successfully applied discount code: ${discountCode}`);
        return true;
      } else {
        console.log(`❌ Failed to apply discount code: ${discountCode} - ${result.message}`);
        return false;
      }
    } catch (error) {
      console.error(`❌ Error applying discount code ${discountCode}:`, error);
      return false;
    }
  }
  
  // Function to apply WELCOME discount as fallback
  async function applyWelcomeDiscount() {
    try {
      console.log(`🔄 Applying WELCOME discount as fallback`);
      
      const result = await globalAddDiscount(
        WELCOME_DISCOUNT_CODE, 
        null, 
        "Checking...", 
        "Applied", 
        "Invalid", 
        "Applied"
      );
      
      if (result.success) {
        console.log(`✅ Successfully applied WELCOME discount`);
      } else {
        console.log(`❌ Failed to apply WELCOME discount: ${result.message}`);
      }
    } catch (error) {
      console.error('❌ Error applying WELCOME discount:', error);
    }
  }
  
  // Function to handle ATC (Add to Cart) logic based on current page
  async function handleATCLogic() {
    try {
      const shouldATC = localStorage.getItem('shouldATC');
      
      if (shouldATC !== 'true') {
        console.log('📝 No shouldATC flag found, skipping ATC logic');
        return;
      }
      
      console.log('🛒 shouldATC flag found, determining what to add to cart...');
      
      // Add 2-second delay before adding anything to cart
      console.log('⏰ Waiting 1 seconds before adding to cart...');
      await new Promise(resolve => setTimeout(resolve, 4000));
      console.log('✅ 1-second delay completed, proceeding with ATC');
      
      const currentUrl = window.location.href;
      const urlPath = new URL(currentUrl).pathname;
      
      // Define URL mappings
      const boxUrls = {
        "Your Stack": "/pages/your-stack",
        "The Energy Stack": "/products/energy-stack",
        "The Sleep Stack": "/products/sleep-stack",
        "The Fitness Stack": "/products/fitness-stack"
      };
      
      const supplementUrls = {
        "Ashwagandha": "/products/ashwagandha",
        "Biotin": "/products/biotin",
        "Calcium": "/products/calcium",
        "CoQ10": "/products/coq10",
        "Folic Acid": "/products/folic-acid",
        "Iron": "/products/iron",
        "Magnesium": "/products/magnesium",
        "Omega 3": "/products/omega-3",
        "Probiotics": "/products/probiotics",
        "Vitamin B12": "/products/vitamin-b12",
        "Vitamin B2": "/products/vitamin-b2",
        "Vitamin B6": "/products/vitamin-b6",
        "Vitamin C": "/products/vitamin-c",
        "Vitamin D": "/products/vitamin-d3",
        "Vitamin E": "/products/vitamin-e",
        "Zinc": "/products/zinc"
      };
      
      // Check if we're on a result page (your-stack)
      if (urlPath.includes('your-stack')) {
        console.log('🎯 Detected result page, adding Your Stack to cart');
        await globalAddBoxToCart(
          "Your Stack",
          null,
          "Adding...",
          "Added",
          "addToCartFromResultPage",
          true
        );
      }
      // Check if we're on a box page
      else if (urlPath.includes('energy-stack')) {
        console.log('🎯 Detected Energy Stack page, adding Energy Stack to cart');
        await globalAddBoxToCart(
          "Energy",
          null,
          "Adding...",
          "Added",
          "addToCartFromResultPage",
          true
        );
      }
      else if (urlPath.includes('sleep-stack')) {
        console.log('🎯 Detected Sleep Stack page, adding Sleep Stack to cart');
        await globalAddBoxToCart(
          "Sleep",
          null,
          "Adding...",
          "Added",
          "addToCartFromResultPage",
          true
        );
      }
      else if (urlPath.includes('fitness-stack')) {
        console.log('🎯 Detected Fitness Stack page, adding Fitness Stack to cart');
        await globalAddBoxToCart(
          "Fitness",
          null,
          "Adding...",
          "Added",
          "addToCartFromResultPage",
          true
        );
      }
      // Check if we're on a supplement page
      else {
        let supplementFound = false;
        
        for (const [supplementName, supplementPath] of Object.entries(supplementUrls)) {
          if (urlPath.includes(supplementPath.replace('/products/', ''))) {
            console.log(`🎯 Detected ${supplementName} page, adding to cart`);
            await globalAddSupplementToCart(
              supplementName,
              null,
              "Adding...",
              "Added to Stack",
              "addToCartFromDrawer",
              true
            );
            supplementFound = true;
            break;
          }
        }
        
        if (!supplementFound) {
          console.log('⚠️ Could not determine page type for ATC, skipping');
        }
      }
      
      // Clear the shouldATC flag from localStorage
      localStorage.removeItem('shouldATC');
      console.log('✅ shouldATC flag cleared from localStorage');
      
    } catch (error) {
      console.error('❌ Error in ATC logic:', error);
      
      // Clear the shouldATC flag even on error
      localStorage.removeItem('shouldATC');
    }
  }
  
  // Main function to handle discount logic
  async function handleDiscountLogic() {
    try {
      // Handle email parameter first
      handleEmailParameter();
      
      // Handle take-the-quiz parameter (before other logic)
      const hasTakeTheQuiz = handleTakeTheQuizParameter();
      if (hasTakeTheQuiz) {
        console.log('🛑 take-the-quiz redirect triggered, stopping further processing');
        return; // Stop execution if redirect happens
      }
      
      // Handle open_discount parameter
      handleOpenDiscountParameter();
      
      // Handle continue parameter
      handleContinueParameter();
      
      // Handle scroll parameter
      handleScrollParameter();
      
      // Get discount code from URL first
      const urlDiscountCode = getAndRemoveDiscountCodeFromURL();
      
      let discountSuccess = false;
      
      if (urlDiscountCode) {
        // Remove WELCOME discount if it exists
        removeWelcomeDiscount();
        
        // Try to apply the URL discount code
        const success = await applyDiscountCode(urlDiscountCode);
        
        if (!success) {
          // If URL discount failed, apply WELCOME as fallback
          await applyWelcomeDiscount();
        }
        discountSuccess = true;
      } else {
        // No URL discount code, check if discounts are already applied
        const appliedDiscounts = getAppliedDiscounts();
        const hasExistingDiscounts = Object.keys(appliedDiscounts).length > 0;
        
        if (hasExistingDiscounts) {
          console.log('📝 Discounts already applied, skipping auto-discount logic');
          discountSuccess = true; // Consider this successful since discounts are already applied
        } else {
          // No existing discounts, apply WELCOME discount
          await applyWelcomeDiscount();
          discountSuccess = true;
        }
      }
      
      // Only handle ATC logic after discount processing is fully completed
      if (discountSuccess) {
        console.log('✅ Discount logic completed successfully, proceeding with ATC logic');
        await handleATCLogic();
      }
      
    } catch (error) {
      console.error('❌ Error in discount logic:', error);
      
      // Fallback to WELCOME discount on any error
      try {
        await applyWelcomeDiscount();
        console.log('✅ WELCOME fallback applied successfully, proceeding with ATC logic');
        await handleATCLogic();
      } catch (fallbackError) {
        console.error('❌ Error applying WELCOME fallback:', fallbackError);
        console.log('⚠️ Both discounts failed, but proceeding with ATC logic since continue=true was provided');
        
        // Still proceed with ATC logic even if both discounts failed
        // The user came with continue=true parameter, so we should honor that
        await handleATCLogic();
      }
    }
  }
  
  // Initialize when DOM is ready
  function initializeWelcomeDiscount() {
    // Wait for global functions to be available
    const checkInterval = setInterval(() => {
      if (typeof globalAddDiscount !== 'undefined' && 
          typeof getAppliedDiscounts !== 'undefined' && 
          typeof saveAppliedDiscounts !== 'undefined') {
        clearInterval(checkInterval);
        
        // Handle discount logic
        handleDiscountLogic();
      }
    }, 100);
    
    // Clear interval after 10 seconds to prevent infinite checking
    setTimeout(() => {
      clearInterval(checkInterval);
    }, 10000);
  }
  
  // Start initialization
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeWelcomeDiscount);
  } else {
    initializeWelcomeDiscount();
  }
  
})();
